import { StyledSelect } from "@ando/ui";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleDashed,
  FilePlus2,
  FlaskConical,
  GitBranchPlus,
  History,
  Loader2,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from "lucide-react";
import * as React from "react";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";

import { apiClient } from "@ando/core";
import { cn } from "@ando/ui";

import { formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmError } from "./HmStates";

/**
 * Hypermem evolution-log tab (v2 port of v1 ``EvolutionLogTab``).
 *
 * Chronological timeline of MetaPrompt agent decisions over the prompt
 * registry. Each entry is audited and immutable; the backend strips PII
 * before persisting, so this view is intentionally read-only.
 *
 * The prompt evolution log is NOT tenant-scoped — ``tenantId`` is purely
 * informational (kept in the public surface for parity with the other
 * hypermem tabs). Data is loaded directly from
 * ``GET /admin/prompts/evolution-log`` via the v2 ``apiClient`` wrapped in
 * TanStack Query.
 */

// ---------------------------------------------------------------------------
// Types — server-shaped (loose; backend may add fields).
// ---------------------------------------------------------------------------

interface HmIdentifiedIssue {
  issue_type?: string;
  description?: string;
  [key: string]: unknown;
}

interface HmEvolutionEntry {
  id: string | number;
  specialist_name: string;
  /** CREATE_VERSION | START_EXPERIMENT | ROLLBACK | NO_ACTION | APPROVE | REJECT | PROMOTE | ... */
  decision: string;
  decision_reasoning?: string;
  outcome?: string | null;
  outcome_notes?: string | null;
  identified_issues?: HmIdentifiedIssue[];
  created_at: string;
  [key: string]: unknown;
}

interface HmEvolutionLogResponse {
  entries: HmEvolutionEntry[];
}

interface HmEvolutionLogFilters {
  specialist: string;
  status: string;
  limit: number;
}

// "all" sentinel keeps filter values explicit in the shared selector.
const ALL_SPECIALISTS = "all";
const ALL_STATUSES = "all";
const DEFAULT_LIMIT = 50;

// Curated list of known specialists — matches the backend prompt registry.
// A specialist returned by the API that isn't here still renders; it just
// won't appear in the dropdown.
const SPECIALISTS: ReadonlyArray<string> = [
  "rag_standard",
  "rag_expert",
  "structured_data",
  "web_search",
  "thinking",
  "consolidation",
  "meta_prompt",
];

const STATUS_OPTIONS: ReadonlyArray<{
  value: string;
  label: MessageDescriptor;
}> = [
  { value: "SUCCESS", label: msg`Successo` },
  { value: "FAILURE", label: msg`Fallimento` },
  { value: "PENDING", label: msg`In corso` },
];

const LIMIT_OPTIONS: ReadonlyArray<number> = [25, 50, 100];

// ---------------------------------------------------------------------------
// Decision presentation tokens
// ---------------------------------------------------------------------------

type DecisionTone = {
  dot: string;
  badge: string;
  Icon: React.ComponentType<{ className?: string }>;
  label: MessageDescriptor;
};

const DECISION_TONES: Record<string, DecisionTone> = {
  CREATE_VERSION: {
    dot: "bg-ando-orange",
    badge: "bg-transparent text-ando-orange border-ando-orange/40",
    Icon: FilePlus2,
    label: msg`Nuova versione`,
  },
  START_EXPERIMENT: {
    dot: "bg-amber-500",
    badge: "bg-transparent text-amber-700 border-amber-300/60",
    Icon: FlaskConical,
    label: msg`Avvio esperimento`,
  },
  ROLLBACK: {
    dot: "bg-red-500",
    badge: "bg-transparent text-red-700 border-red-300/60",
    Icon: RotateCcw,
    label: msg`Rollback`,
  },
  NO_ACTION: {
    dot: "bg-ink-3/40",
    badge: "bg-cream/60 text-ink-2 border-hairline",
    Icon: CircleDashed,
    label: msg`Nessuna azione`,
  },
  APPROVE: {
    dot: "bg-emerald-500",
    badge: "bg-transparent text-emerald-700 border-emerald-300/60",
    Icon: ThumbsUp,
    label: msg`Approvato`,
  },
  REJECT: {
    dot: "bg-orange-500",
    badge: "bg-transparent text-orange-700 border-orange-300/60",
    Icon: ThumbsDown,
    label: msg`Respinto`,
  },
  PROMOTE: {
    dot: "bg-emerald-600",
    badge: "bg-transparent text-emerald-700 border-emerald-300/60",
    Icon: GitBranchPlus,
    label: msg`Promosso`,
  },
};

const DEFAULT_TONE: DecisionTone = {
  dot: "bg-ink-3/40",
  badge: "bg-cream/60 text-ink-2 border-hairline",
  Icon: History,
  label: msg`Decisione`,
};

function getDecisionTone(decision: string | undefined | null): DecisionTone {
  if (!decision) return DEFAULT_TONE;
  return DECISION_TONES[decision] ?? DEFAULT_TONE;
}

type OutcomeTone = {
  badge: string;
  Icon: React.ComponentType<{ className?: string }>;
  label: MessageDescriptor;
};

const OUTCOME_TONES: Record<string, OutcomeTone> = {
  SUCCESS: {
    badge: "bg-transparent text-emerald-700 border-emerald-300/60",
    Icon: CheckCircle2,
    label: msg`Successo`,
  },
  FAILURE: {
    badge: "bg-transparent text-red-700 border-red-300/60",
    Icon: XCircle,
    label: msg`Fallimento`,
  },
  PENDING: {
    badge: "bg-transparent text-amber-700 border-amber-300/60",
    Icon: CircleDashed,
    label: msg`In corso`,
  },
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

function buildQueryString(filters: HmEvolutionLogFilters): string {
  const params = new URLSearchParams();
  if (filters.specialist !== ALL_SPECIALISTS) {
    params.set("specialist_name", filters.specialist);
  }
  if (filters.status !== ALL_STATUSES) {
    params.set("status", filters.status);
  }
  params.set("limit", String(filters.limit));
  return params.toString();
}

function useEvolutionLog(filters: HmEvolutionLogFilters) {
  return useQuery<HmEvolutionLogResponse>({
    queryKey: ["admin", "hypermem", "evolution-log", filters],
    queryFn: () => {
      const qs = buildQueryString(filters);
      return apiClient.get<HmEvolutionLogResponse>(`/admin/prompts/evolution-log?${qs}`);
    },
  });
}

// ---------------------------------------------------------------------------
// Inline subcomponents
// ---------------------------------------------------------------------------

function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center text-ink-3 text-sm">
      <History className="mb-2 h-8 w-8 opacity-50" aria-hidden />
      <p>{message}</p>
    </div>
  );
}

function SkeletonList({ count = 5 }: { count?: number }): React.ReactElement {
  return (
    <div className="space-y-3" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-20 w-full animate-pulse rounded-xl bg-ink-3/10" />
      ))}
    </div>
  );
}

function DecisionIcon({ decision }: { decision: string }): React.ReactElement {
  const tone = getDecisionTone(decision);
  const { Icon } = tone;
  return (
    <div
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full text-white shadow-sm",
        tone.dot,
      )}
      aria-hidden
    >
      <Icon className="h-4 w-4" />
    </div>
  );
}

function DecisionBadge({ decision }: { decision: string }): React.ReactElement {
  const { i18n } = useLingui();
  const tone = getDecisionTone(decision);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium text-[11px]",
        tone.badge,
      )}
    >
      {i18n._(tone.label)}
    </span>
  );
}

function OutcomeBadge({
  outcome,
}: {
  outcome: string | null | undefined;
}): React.ReactElement | null {
  const { i18n } = useLingui();
  if (!outcome) return null;
  const tone = OUTCOME_TONES[outcome.toUpperCase()];
  if (!tone) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-hairline bg-cream/60 px-2 py-0.5 text-[11px] text-ink-2">
        {outcome}
      </span>
    );
  }
  const { Icon } = tone;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium text-[11px]",
        tone.badge,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {i18n._(tone.label)}
    </span>
  );
}

function EvolutionEntryRow({
  entry,
}: {
  entry: HmEvolutionEntry;
}): React.ReactElement {
  const issues = entry.identified_issues ?? [];
  return (
    <div className="flex gap-3 rounded-r-md border-hairline border-l-2 py-2 pl-4 transition-colors hover:bg-cream/40">
      <div className="flex-shrink-0 pt-0.5">
        <DecisionIcon decision={entry.decision} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2 text-sm">
          <strong className="truncate font-semibold text-ink-1">{entry.specialist_name}</strong>
          <DecisionBadge decision={entry.decision} />
          <OutcomeBadge outcome={entry.outcome} />
          <span className="ml-auto whitespace-nowrap text-[11px] text-ink-3">
            {formatTimestamp(entry.created_at)}
          </span>
        </div>
        {entry.decision_reasoning ? (
          <p className="mt-1 line-clamp-2 text-ink-2 text-xs leading-relaxed">
            {entry.decision_reasoning}
          </p>
        ) : null}
        {entry.outcome_notes ? (
          <p className="mt-1 text-[11px] text-ink-3 italic">
            <Trans>Esito: {entry.outcome_notes}</Trans>
          </p>
        ) : null}
        {issues.length > 0 ? (
          <details className="group mt-2">
            <summary className="flex cursor-pointer select-none list-none items-center gap-1 text-[11px] text-ink-3 hover:text-ink-1">
              <span className="group-open:hidden" aria-hidden>
                ▸
              </span>
              <span className="hidden group-open:inline" aria-hidden>
                ▾
              </span>
              <Plural
                value={issues.length}
                one="# issue identificata"
                other="# issue identificate"
              />
            </summary>
            <ul className="mt-2 space-y-1 pl-3 text-[11px] text-ink-3">
              {issues.map((issue, idx) => (
                <li key={idx} className="leading-snug">
                  <span className="font-medium text-ink-1">{issue.issue_type ?? "—"}</span>
                  {issue.description ? (
                    <>
                      <span className="mx-1">·</span>
                      <span>{issue.description}</span>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SELECT_CLASS =
  "rounded-lg border border-hairline bg-cream/40 px-2.5 py-1.5 text-sm text-ink-1 transition-colors hover:bg-cream/60 focus:outline-none focus:ring-2 focus:ring-ando-orange/40";

export function HmEvolutionLogTab({
  tenantId: _tenantId,
}: {
  tenantId: string | null;
}): React.ReactElement {
  const { t, i18n } = useLingui();
  // ``tenantId`` is informational only — the prompt evolution log is global.
  const [specialist, setSpecialist] = React.useState<string>(ALL_SPECIALISTS);
  const [status, setStatus] = React.useState<string>(ALL_STATUSES);
  const [limit, setLimit] = React.useState<number>(DEFAULT_LIMIT);

  const filters = React.useMemo<HmEvolutionLogFilters>(
    () => ({ specialist, status, limit }),
    [specialist, status, limit],
  );

  const { data, isLoading, isError, error, isFetching, refetch } = useEvolutionLog(filters);

  const entries = data?.entries ?? [];
  const errorMessage = error instanceof Error ? error.message : t`Errore sconosciuto`;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-cream/40 p-4">
        {/* Header + filters */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="flex items-center gap-2 font-semibold text-base text-ink-1">
              <History className="h-4 w-4 text-ando-orange" aria-hidden />
              <Trans>Evolution log</Trans>
            </h4>
            <p className="mt-1 text-ink-3 text-sm">
              <Trans>
                Timeline cronologica delle decisioni MetaPrompt sui prompt. Ogni evento è auditato e
                immutabile.
              </Trans>
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <StyledSelect
              value={specialist}
              onChange={(e) => setSpecialist(e.target.value)}
              className={SELECT_CLASS}
              aria-label={t`Filtra per specialist`}
            >
              <option value={ALL_SPECIALISTS}>{t`Tutti gli specialist`}</option>
              {SPECIALISTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </StyledSelect>
            <StyledSelect
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={SELECT_CLASS}
              aria-label={t`Filtra per stato`}
            >
              <option value={ALL_STATUSES}>{t`Tutti gli stati`}</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {i18n._(o.label)}
                </option>
              ))}
            </StyledSelect>
            <StyledSelect
              value={String(limit)}
              onChange={(e) => setLimit(Number(e.target.value))}
              className={SELECT_CLASS}
              aria-label={t`Numero di voci`}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {t`Ultime ${n}`}
                </option>
              ))}
            </StyledSelect>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-1.5 text-ink-3 text-xs transition-colors hover:text-ink-1 disabled:opacity-50"
              aria-label={t`Aggiorna log`}
            >
              <Loader2 className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} aria-hidden />
              <Trans>Aggiorna</Trans>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="mt-4">
          {isError && !data ? (
            <HmError
              message={<Trans>Impossibile caricare l'evolution log</Trans>}
              onRetry={() => refetch()}
            />
          ) : isLoading ? (
            <SkeletonList count={5} />
          ) : entries.length === 0 ? (
            <EmptyState message={t`Nessuna decisione registrata`} />
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <EvolutionEntryRow key={String(entry.id)} entry={entry} />
              ))}
            </div>
          )}

          {isError && data ? (
            <p className="mt-3 text-amber-600 text-xs">
              <Trans>Ultima ricarica fallita: {errorMessage}</Trans>
            </p>
          ) : null}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
        <span className="text-ink-3/80">
          <Trans>Legenda:</Trans>
        </span>
        {(
          [
            "CREATE_VERSION",
            "START_EXPERIMENT",
            "ROLLBACK",
            "NO_ACTION",
            "APPROVE",
            "REJECT",
          ] as const
        ).map((d) => {
          const tone = getDecisionTone(d);
          return (
            <span key={d} className="inline-flex items-center gap-1">
              <span className={cn("h-2 w-2 rounded-full", tone.dot)} aria-hidden />
              {i18n._(tone.label)}
            </span>
          );
        })}
      </div>

      <p className="text-ink-3 text-xs">
        <Trans>
          Aggregato (no PII) · Registro globale dei prompt · Ordine cronologico decrescente.
        </Trans>
      </p>
    </div>
  );
}

export default HmEvolutionLogTab;
